import { groups } from "d3";
import { min, max, scaleLog } from "d3";
import {
  getOrdersOfMagnitudeDifference,
  spectrumStartPercent,
  spectrumEndPercent,
  colorRangeStart,
  colorRangeEnd,
} from "./Utils";
import {
  defaultMapFillColor,
  primaryCountryColor,
  defaultMapStrokeColor,
  primaryCountryStrokeColor,
} from "./Utils";
import {
  worldGroupDatum,
  getLocationLevelFromStringLocationId,
  computeMonetaryValueByTradeFlow,
  computeTotalSumByTradeFlow,
} from "@atlas-int/data";
import { LocationLevel } from "@atlas-int/data/graphql";
import { TradeDirection, TradeFlow } from "@atlas-int/data/types";
import {
  createLocationHierarchyIndex,
  enrichLocationWithHierarchyFast,
} from "@atlas-int/data";
import type { FeatureCollection, Feature, Geometry } from "geojson";
import type { TradeDataItem } from "@atlas-int/data/types";

interface LocationGroupMember {
  countryId?: string;
  groupId?: string;
  iso3Code?: string;
  members?: string[];
  [key: string]: unknown;
}

interface GeoFeatureProperties {
  iso_alpha3?: string;
  name?: string;
  countryId?: string;
  nameEn?: string;
  importValue?: number;
  exportValue?: number;
  exportsColor?: string;
  importsColor?: string;
  strokeColor?: string;
  [key: string]: unknown;
}

interface GroupAndSummarizeParams {
  data: TradeDataItem[];
  location: string;
  locationLevel: LocationLevel | undefined;
  tradeFlow: TradeFlow;
}

export const groupAndSummarizeByLocationLevel = ({
  data,
  location,
  locationLevel,
  tradeFlow,
}: GroupAndSummarizeParams): TradeDataItem[] => {
  let groupedFilteredData: TradeDataItem[];

  if (locationLevel === LocationLevel.Country) {
    groupedFilteredData = groups(
      data,
      (d: TradeDataItem) => d.partnerCountryId,
    ).map((d: [string, TradeDataItem[]]) => {
      const partnerCountryId = d[0];
      const partnerLevel = d[1][0].partnerLevel;
      const sumExportValue = computeTotalSumByTradeFlow({
        data: d[1],
        tradeFlow,
        tradeDirection: TradeDirection.Exports,
      });
      const sumImportValue = computeTotalSumByTradeFlow({
        data: d[1],
        tradeFlow,
        tradeDirection: TradeDirection.Imports,
      });

      return {
        ...d[1][1],
        countryId: location,
        locationLevel: "country",
        partnerCountryId,
        partnerLevel,
        exportValue: sumExportValue,
        importValue: sumImportValue,
        productsData: d[1],
      };
    });
  } else if (locationLevel === LocationLevel.Group) {
    groupedFilteredData = groups(
      data,
      (d: TradeDataItem) => d.partnerCountryId,
    ).map((d: [string, TradeDataItem[]]) => {
      const partnerCountryId = d[0];
      const partnerLevel = d[1][0].partnerLevel;
      const sumExportValue = computeTotalSumByTradeFlow({
        data: d[1],
        tradeFlow,
        tradeDirection: TradeDirection.Exports,
      });
      const sumImportValue = computeTotalSumByTradeFlow({
        data: d[1],
        tradeFlow,
        tradeDirection: TradeDirection.Imports,
      });

      return {
        ...d[1][1],
        groupId: location,
        partnerCountryId,
        locationLevel: "country",
        partnerLevel,
        exportValue: sumExportValue,
        importValue: sumImportValue,
        productsData: d[1],
      };
    });
  } else {
    groupedFilteredData = [];
  }

  return groupedFilteredData;
};

interface TransformLocationsParams {
  geojson: FeatureCollection<Geometry, GeoFeatureProperties>;
  tradeFlow: TradeFlow;
  data: TradeDataItem[];
  location: string;
  regions: LocationGroupMember[];
  subregions: LocationGroupMember[];
  countries: LocationGroupMember[];
  /** When true (World→World Markets query) the API returns exportValue/importValue with
   *  inverted semantics.  This flag causes the transform to swap them in feature properties
   *  so that downstream consumers (tooltip, download) can use the original user-facing
   *  tradeDirection without a separate correction step. */
  isWorldToWorldQuery?: boolean;
}

function createSpectrumScale(
  dataWithParents: TradeDataItem[],
  tradeFlow: TradeFlow,
  tradeDirection: TradeDirection,
) {
  const filtered = dataWithParents.filter((country) => {
    const val = computeMonetaryValueByTradeFlow({
      datum: country,
      tradeFlow,
      tradeDirection,
    });
    return val && val > 0;
  });
  const values = filtered.map((country) =>
    computeMonetaryValueByTradeFlow({
      datum: country,
      tradeFlow,
      tradeDirection,
    }),
  );
  const minVal = min(values, (v) => v) as number;
  const maxVal = max(values, (v) => v) as number;
  const [domainStart, domainEnd] = scaleLog()
    .domain([minVal, maxVal])
    .nice()
    .domain();
  const ordersOfMag = getOrdersOfMagnitudeDifference(domainStart, domainEnd);
  const specStart =
    10 ** (Math.log10(domainStart) + ordersOfMag * spectrumStartPercent);
  const specEnd =
    10 ** (Math.log10(domainStart) + ordersOfMag * spectrumEndPercent);
  const scale = scaleLog<string, string>()
    .domain([specStart, specEnd])
    .range([colorRangeStart, colorRangeEnd]);
  return { scale, spectrumStart: specStart, spectrumEnd: specEnd };
}

function getColorForValue(
  value: number | undefined,
  spectrumStart: number,
  spectrumEnd: number,
  scale: (v: number) => string,
): string {
  if (!value || value <= 0) return defaultMapFillColor;
  if (value > spectrumEnd) return colorRangeEnd;
  if (value < spectrumStart) return colorRangeStart;
  return scale(value);
}

const transformLocations = ({
  geojson,
  tradeFlow,
  data,
  location,
  regions,
  subregions,
  countries,
  isWorldToWorldQuery = false,
}: TransformLocationsParams): {
  type: string;
  features: Feature<Geometry, GeoFeatureProperties>[];
} => {
  const locationLevel = getLocationLevelFromStringLocationId(location);

  let selectedLocationIso3CodeSet = new Set();

  if (locationLevel === LocationLevel.Country) {
    let matchedIso3Code = countries.find(
      ({ countryId }: LocationGroupMember) => countryId === location,
    )?.["iso3Code"];
    if (matchedIso3Code) selectedLocationIso3CodeSet.add(matchedIso3Code);
  } else if (locationLevel === LocationLevel.Group) {
    if (location !== worldGroupDatum.groupId) {
      let groupMembers = [...regions, ...subregions].find(
        ({ groupId }: LocationGroupMember) => groupId === location,
      )?.["members"];
      if (groupMembers) {
        groupMembers.forEach((memberCountryId: string) => {
          let matchedIso3Code = countries.find(
            ({ countryId }: LocationGroupMember) =>
              countryId === memberCountryId,
          )?.["iso3Code"];
          if (matchedIso3Code) selectedLocationIso3CodeSet.add(matchedIso3Code);
        });
      }
    }
  }

  const groupedFilteredData = groupAndSummarizeByLocationLevel({
    data,
    location,
    locationLevel,
    tradeFlow,
  });

  // Step 5: Pre-compute location hierarchy index for O(1) lookups
  const locationIndex = createLocationHierarchyIndex(
    regions,
    subregions,
    countries,
  );

  const dataWithParents = groupedFilteredData.map((d: TradeDataItem) => {
    const partnerCountryId = d.partnerCountryId;

    const locationHierarchy = enrichLocationWithHierarchyFast(
      partnerCountryId as unknown as number,
      locationIndex,
    );

    return {
      ...d,
      id: d.partnerCountryId,
      topLevelParent: locationHierarchy.topLevelParent,
      nameEn: locationHierarchy.nameEn,
      hybrid_level_1: locationHierarchy.hybrid_level_1,
      hybrid_level_2: locationHierarchy.hybrid_level_2,
      iso3Code: locationHierarchy.countryIso3Code,
    };
  });

  const {
    scale: spectrumScaleExports,
    spectrumStart: spectrumStartValueExports,
    spectrumEnd: spectrumEndValueExports,
  } = createSpectrumScale(dataWithParents, tradeFlow, TradeDirection.Exports);
  const {
    scale: spectrumScaleImports,
    spectrumStart: spectrumStartValueImports,
    spectrumEnd: spectrumEndValueImports,
  } = createSpectrumScale(dataWithParents, tradeFlow, TradeDirection.Imports);

  const apiNameByIso3 = new Map<string, string>();
  countries.forEach((c) => {
    const iso3 = c.iso3Code as string | undefined;
    const apiName =
      ((c as Record<string, unknown>).nameShortEn as string | undefined) ||
      ((c as Record<string, unknown>).nameEn as string | undefined);
    if (iso3 && apiName) apiNameByIso3.set(iso3, apiName);
  });

  const transformedFeatures = geojson.features.map(
    (feature: Feature<Geometry, GeoFeatureProperties>) => {
      const featureIso3Code = feature.properties?.iso_alpha3;
      const matchingDatumWithParent = dataWithParents.find(
        (country) => country.iso3Code === featureIso3Code,
      );
      let importValue: number | undefined;
      let exportValue: number | undefined;
      let exportsColor: string | undefined;
      let importsColor: string | undefined;
      let strokeColor: string | undefined;
      let countryId: string | undefined;
      // Prefer the API name for every feature; fall back to the GeoJSON name only
      // when the API has no entry for this ISO3 code.
      let nameEn: string | undefined = matchingDatumWithParent
        ? matchingDatumWithParent.nameEn
        : featureIso3Code
          ? apiNameByIso3.get(featureIso3Code)
          : undefined;
      if (selectedLocationIso3CodeSet.has(featureIso3Code)) {
        importValue = undefined;
        exportValue = undefined;
        exportsColor = primaryCountryColor;
        importsColor = primaryCountryColor;
        strokeColor = primaryCountryStrokeColor;
        countryId = undefined;
      } else if (matchingDatumWithParent) {
        importValue = computeMonetaryValueByTradeFlow({
          datum: matchingDatumWithParent,
          tradeFlow,
          tradeDirection: TradeDirection.Imports,
        });
        exportValue = computeMonetaryValueByTradeFlow({
          datum: matchingDatumWithParent,
          tradeFlow,
          tradeDirection: TradeDirection.Exports,
        });
        exportsColor = getColorForValue(
          exportValue,
          spectrumStartValueExports,
          spectrumEndValueExports,
          spectrumScaleExports,
        );
        importsColor = getColorForValue(
          importValue,
          spectrumStartValueImports,
          spectrumEndValueImports,
          spectrumScaleImports,
        );

        // For World→World Markets queries the API returns inverted field semantics:
        // exportValue = what was exported TO this country (i.e. its imports)
        // importValue = what was exported FROM this country (i.e. its exports)
        // Swap both values and colors so that downstream code (tooltip, download)
        // can use the original user-facing tradeDirection without correction.
        if (isWorldToWorldQuery) {
          [exportValue, importValue] = [importValue, exportValue];
          [exportsColor, importsColor] = [importsColor, exportsColor];
        }

        countryId = String(matchingDatumWithParent.partnerCountryId);
        strokeColor = defaultMapStrokeColor;
      } else {
        importValue = undefined;
        exportValue = undefined;
        exportsColor = defaultMapFillColor;
        importsColor = defaultMapFillColor;
        strokeColor = defaultMapStrokeColor;
        countryId = undefined;
      }

      const updatedFeature = Object.assign({}, feature);

      updatedFeature.properties = {
        ...feature.properties,
        countryId,
        importValue,
        exportValue,
        exportsColor,
        importsColor,
        strokeColor,
        nameEn,
      };

      return updatedFeature;
    },
  );

  return { type: "FeatureCollection", features: transformedFeatures };
};

export default transformLocations;
